/**
 * Раздел «Печать» внизу карточки договора: кто и когда печатал договор (версии, ушедшие на печать, —
 * скачать PDF по праву `passport_view`: в нём паспорт клиента) и бланки сделки — выдачи, возврата, сбора.
 * `GET /api/contracts/{id}/prints?deal=`, `…/prints/{task}/pdf`. Спека
 * `docs/specs/2026-10-23-contract-prints-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";

import { apiFetch } from "@/lib/api";
import { PASSPORT_VIEW, useCan } from "@/lib/access";

type ContractPrint = { id: number; at: string | null; user: string | null; copies: number; status: string; printer: string; file: boolean };
type FormPrint = { id: number; kind: string; at: string | null; user: string | null; status: string };
type Prints = { contract: ContractPrint[]; forms: FormPrint[] };

const when = (at: string | null) => (at ? `${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(0, 4)} ${at.slice(11, 16)}` : "—");

async function download(dogovorId: number, taskId: number): Promise<void> {
  const r = await fetch(`/api/contracts/${dogovorId}/prints/${taskId}/pdf`, { credentials: "same-origin" });
  if (!r.ok) throw new Error((await r.json().catch(() => null))?.error?.[0]?.message ?? `ошибка ${r.status}`);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `Договор ${dogovorId} печать ${taskId}.pdf`;
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ContractPrints({ dogovorId, dealId }: { dogovorId: number; dealId: number }) {
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const canFile = useCan(PASSPORT_VIEW);
  const q = useQuery({
    queryKey: ["contract-prints", dogovorId, dealId],
    queryFn: () => apiFetch<Prints>(`/api/contracts/${dogovorId}/prints?deal=${dealId}`),
  });
  const n = q.data ? q.data.contract.length + q.data.forms.length : null;
  const last = q.data?.contract[0];
  const get = (id: number) => {
    setErr(null);
    setBusy(id);
    download(dogovorId, id)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(null));
  };
  return (
    <div className="flex flex-col gap-1.5" data-annot={`печать договора ${dogovorId}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="self-start text-[12px] text-accent-text hover:underline">
        {open ? "Скрыть печать" : `Печать${n != null ? ` (${n})` : ""}`}
        {!open && last && <span className="text-fg-muted"> · договор печатал(а) {last.user ?? "—"} {when(last.at)}</span>}
      </button>
      {q.isError && <div className="text-[12px] text-err">Печать не загрузилась: {q.error.message}</div>}
      {open && q.data && (
        <div className="flex flex-col gap-2 text-[12px]">
          <div>
            <div className="mb-0.5 font-medium">Договор</div>
            {q.data.contract.length === 0 && <div className="text-fg-muted">Через CRM договор не печатали.</div>}
            <ul className="flex flex-col gap-0.5">
              {q.data.contract.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-x-2">
                  <span className="tabular-nums">{when(p.at)}</span>
                  <span>{p.user ?? "—"}</span>
                  <span className="text-fg-muted">
                    {p.printer || "принтер не указан"} · {p.copies} экз. · {p.status}
                  </span>
                  {p.file && canFile && (
                    <button type="button" disabled={busy === p.id} onClick={() => get(p.id)} className="inline-flex items-center gap-0.5 text-accent-text hover:underline disabled:opacity-50" title="Скачать PDF той версии договора, что ушла на печать">
                      <Download size={12} aria-hidden /> {busy === p.id ? "скачиваю…" : "PDF"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-0.5 font-medium">Бланки сделки</div>
            {q.data.forms.length === 0 && <div className="text-fg-muted">Бланков через CRM не печатали.</div>}
            <ul className="flex flex-col gap-0.5">
              {q.data.forms.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-x-2">
                  <span className="tabular-nums">{when(f.at)}</span>
                  <span className="font-medium">{f.kind}</span>
                  <span>{f.user ?? "—"}</span>
                  <span className="text-fg-muted">{f.status}</span>
                </li>
              ))}
            </ul>
          </div>
          {err && <div className="text-err">PDF не скачался: {err}</div>}
        </div>
      )}
    </div>
  );
}
